import type { ElementType, ReactNode } from "react"
import { ShoppingBag, Utensils } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { groupRuleSummary } from "@/features/extras/group-rules"
import { formatMoney, parseDecimal } from "./money"
import type { ItemExtra, ItemExtraGroup, OrderServiceType } from "./types"
import { t } from "@/lib/i18n"

type DishHeadingProps = {
  name: string
  // Both optional on the serialized item, so the prop mirrors the payload rather
  // than making every caller normalise it.
  emoji?: string | null
  /** Suppressed when a photo is showing: the picture already says what it is. */
  showEmoji: boolean
  price: number
  /**
   * The menu price, when a promotion made `price` something other than it.
   * Struck through beside the figure the diner will pay.
   */
  originalPrice?: number | null
  /** The rule's own name, as the restaurant wrote it. */
  promotionName?: string | null
  currency: string
  description?: string | null
  /**
   * The element that carries the name. The drawer hands its `DrawerTitle` here so
   * Radix still gets an accessible label; the deep-link page leaves it an `h1`.
   * Passing the element instead of duplicating the block is the point — see below.
   */
  titleComponent?: ElementType
  /** Same, for the description, so a drawer with one is properly described. */
  descriptionComponent?: ElementType
  className?: string
}

/**
 * Name, price, description — the identity of a dish, in one place.
 *
 * It lives here because two shells show the same dish: the drawer the menu opens,
 * and the deep-link page at `/menu/items/:id`. They each used to build this block
 * themselves and had drifted apart, which is how the drawer ended up with a
 * centred price: `DrawerHeader` carries a
 * `group-data-[vaul-drawer-direction=bottom]:text-center` that outranks a plain
 * `text-left` from the caller, so the flex title escaped it and the block-level
 * price did not. One component means the two cannot disagree again, and a change
 * of mind about the type scale is one edit rather than two.
 */
export function DishHeading({
  name,
  emoji,
  showEmoji,
  price,
  originalPrice,
  promotionName,
  currency,
  description,
  titleComponent: Title = "h1",
  descriptionComponent: Description = "p",
  className,
}: DishHeadingProps) {
  // Only when it actually differs. A rule whose arithmetic came out to the same
  // figure -- a rounding floor, a ceiling that ate the whole reduction -- would
  // otherwise show the diner "$120 $120" and a promise of nothing.
  const promoted = originalPrice != null && originalPrice > price

  return (
    <div className={cn("text-left", className)}>
      {/* `text-lg` is the ceiling on a product page, and the name sits at it.
          The price then cannot also be `text-lg` without flattening the two into
          one another, so the step down is weight and colour rather than size:
          the name is the only large thing here, and the figure stays a figure —
          semibold, foreground, tabular. */}
      <Title className="flex items-center gap-2 text-lg font-semibold leading-tight tracking-tight">
        {showEmoji && emoji && (
          <span className="text-lg leading-none" aria-hidden="true">
            {emoji}
          </span>
        )}
        <span className="min-w-0">{name}</span>
      </Title>
      {/* The promotion's name reads before the figures, so the struck-through
          price has already been explained by the time it is seen. Neutral
          `secondary`, the same mark the featured carousel uses: colour on a
          price this size would break the dot rule outright. */}
      {promoted && promotionName && (
        <Badge variant="secondary" className="mt-1.5 flex w-fit" data-testid="dish-detail-promotion">
          {promotionName}
        </Badge>
      )}
      <p className="mt-1 flex items-baseline gap-1.5 text-sm font-semibold tabular-nums text-foreground">
        {promoted && (
          <span className="text-xs font-normal tabular-nums text-muted-foreground line-through">
            <span className="sr-only">{t("ui.react.features.user.dish_price.original")} </span>
            {formatMoney(originalPrice, { currency })}
          </span>
        )}
        <span data-testid="dish-detail-price">
          {promoted && <span className="sr-only">{t("ui.react.features.user.dish_price.promoted")} </span>}
          {formatMoney(price, { currency })}
        </span>
      </p>
      {description && (
        <Description className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {description}
        </Description>
      )}
    </div>
  )
}

type DishCustomizeProps = {
  groups: ItemExtraGroup[]
  currency: string
  selectedIds: Set<number>
  toggleExtra: (groupId: number, extraId: number) => void
  clearGroup: (groupId: number) => void
  note: string
  setNote: (value: string) => void
  /** Dine-in only: pick eat-in vs to-go per dish. Omit to hide (e.g. pickup context). */
  serviceType?: OrderServiceType
  onServiceTypeChange?: (value: OrderServiceType) => void
}

export function DishCustomize({
  groups,
  currency,
  selectedIds,
  toggleExtra,
  clearGroup,
  note,
  setNote,
  serviceType,
  onServiceTypeChange,
}: DishCustomizeProps) {
  const showServiceType = Boolean(serviceType && onServiceTypeChange)
  return (
    <div className="space-y-6">
      {showServiceType && (
        <Group title={t("ui.react.features.user.dish_detail.service_type_question")}>
          <div className="grid grid-cols-2 gap-2">
            <ServiceTypeOption
              active={serviceType === "eat_in"}
              onClick={() => onServiceTypeChange?.("eat_in")}
              icon={<Utensils className="size-4" />}
              label={t("ui.react.features.user.dish_detail.eat_in")}
              testId="dish-service-eat_in"
            />
            <ServiceTypeOption
              active={serviceType === "take_out"}
              onClick={() => onServiceTypeChange?.("take_out")}
              icon={<ShoppingBag className="size-4" />}
              label={t("ui.react.features.user.dish_detail.take_out")}
              testId="dish-service-take_out"
            />
          </div>
        </Group>
      )}

      {groups.length > 0 && (
        <div className="space-y-6" data-testid="dish-extra-groups">
          {groups.map((group) => (
            <ExtraGroupSection
              key={group.id}
              group={group}
              currency={currency}
              selectedIds={selectedIds}
              toggleExtra={toggleExtra}
              clearGroup={clearGroup}
            />
          ))}
        </div>
      )}

      <Group title={t("ui.react.features.user.dish_detail.notes_kitchen")}>
        <Textarea
          value={note}
          data-testid="dish-notes-input"
          onChange={(event) => setNote(event.target.value)}
          placeholder={t("ui.react.features.user.dish_detail.cilantro_allergies_etc")}
          className="min-h-16 resize-none"
        />
      </Group>
    </div>
  )
}

// Human-readable selection rule for a group's header. Replaces the old
// "selected/max" fraction (which diners read as a required quota) with a static
// instruction. Single-choice groups keep the compact "Required" affordance (or
// nothing, since the "None" row already signals optional); every multi-select
// group delegates to the shared groupRuleSummary so staff and diner read
// identically ("Choose between 1 and 3", "Optional, up to 2").
function groupRuleLabel(group: ItemExtraGroup): string | null {
  const isRadio = group.maxSelect === 1

  if (isRadio) {
    return group.minSelect >= 1 ? t("ui.user.dish.required") : null
  }

  return groupRuleSummary(group)
}

function ExtraGroupSection({
  group,
  currency,
  selectedIds,
  toggleExtra,
  clearGroup,
}: {
  group: ItemExtraGroup
  currency: string
  selectedIds: Set<number>
  toggleExtra: (groupId: number, extraId: number) => void
  clearGroup: (groupId: number) => void
}) {
  const isRadio = group.maxSelect === 1
  const required = group.minSelect >= 1
  const selectedCount = group.extras.filter((extra) => selectedIds.has(extra.id)).length
  const ruleLabel = groupRuleLabel(group)
  const groupTestId = `dish-extra-group-${group.id}`
  const radioName = `dish-extra-group-${group.id}-radio`
  // A multi-select group at its cap freezes its unchecked options, so the diner
  // can't overshoot maxSelect into a state where add-to-cart silently dies.
  // Radios never hit this (picking one clears its siblings).
  const atCap = !isRadio && group.maxSelect != null && selectedCount >= group.maxSelect

  return (
    <section data-testid={groupTestId}>
      <header className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold tracking-tight">{group.name}</h2>
        {ruleLabel && (
          <span
            className={cn(
              "shrink-0 text-xs font-medium",
              required ? "text-foreground" : "text-muted-foreground"
            )}
            data-testid={required ? `${groupTestId}-required` : `${groupTestId}-rule`}
          >
            {ruleLabel}
          </span>
        )}
      </header>

      <div className="divide-y divide-border/60">
        {isRadio && group.minSelect === 0 && (
          <label
            data-testid={`${groupTestId}-none`}
            className="flex min-h-11 cursor-pointer items-center gap-3 py-2"
          >
            <input
              type="radio"
              name={radioName}
              className="size-4 accent-primary"
              checked={selectedCount === 0}
              onChange={() => clearGroup(group.id)}
            />
            <span className="min-w-0 flex-1 text-sm font-medium text-muted-foreground">
              {t("ui.user.dish.no_extra")}
            </span>
          </label>
        )}

        {group.extras.map((extra) => {
          const checked = selectedIds.has(extra.id)
          return (
            <ExtraRow
              key={extra.id}
              extra={extra}
              currency={currency}
              checked={checked}
              disabled={atCap && !checked}
              isRadio={isRadio}
              radioName={radioName}
              onSelect={() => toggleExtra(group.id, extra.id)}
            />
          )
        })}
      </div>
    </section>
  )
}

function ExtraRow({
  extra,
  currency,
  checked,
  disabled = false,
  isRadio,
  radioName,
  onSelect,
}: {
  extra: ItemExtra
  currency: string
  checked: boolean
  /** True when the group is at its maxSelect cap and this row isn't one of the picks. */
  disabled?: boolean
  isRadio: boolean
  radioName: string
  onSelect: () => void
}) {
  const soldOut = extra.status === "sold_out"
  const blocked = soldOut || disabled
  const price = parseDecimal(extra.price)

  return (
    <label
      data-testid={`dish-extra-${extra.id}`}
      data-active={checked ? "true" : "false"}
      className={cn(
        "flex min-h-11 items-center gap-3 py-2",
        blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      )}
    >
      {isRadio ? (
        <input
          type="radio"
          name={radioName}
          className="size-4 accent-primary disabled:cursor-not-allowed"
          checked={checked}
          onChange={onSelect}
          disabled={blocked}
          aria-label={extra.name}
        />
      ) : (
        <Checkbox checked={checked} onCheckedChange={onSelect} aria-label={extra.name} disabled={blocked} />
      )}
      <span className="min-w-0 flex-1 text-sm font-medium">{extra.name}</span>
      <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
        {soldOut
          ? t("ui.react.components.features.menu.menu_grid.soldout")
          : price > 0
            ? `+${formatMoney(price, { currency })}`
            : t("ui.user.dish.free")}
      </span>
    </label>
  )
}

type DishOrderControlsProps = {
  qty: number
  setQty: (value: number) => void
  total: number
  currency: string
  /**
   * Why the dish cannot be added, when it cannot. `no_table` is the dine-in
   * case: no open bill to add to. It used to be expressed as `billId === null`,
   * which read as "no bill, no ordering" and was right for exactly one context —
   * a pickup cart has no bill on purpose and would have been permanently
   * disabled by it.
   */
  blockedReason?: "no_table" | "closed" | null
  submitting: boolean
  canSubmit: boolean
  /** True when the item can't be ordered at all (e.g. a required group is fully sold out). */
  unavailable?: boolean
  onAdd: () => void
}

export function DishOrderControls({
  qty,
  setQty,
  total,
  currency,
  blockedReason = null,
  submitting,
  canSubmit,
  unavailable = false,
  onAdd,
}: DishOrderControlsProps) {
  const label = unavailable
    ? t("ui.user.dish.unavailable")
    : blockedReason === "no_table"
      ? t("ui.user.dish.no_active_table")
      : blockedReason === "closed"
        ? t("ui.react.features.user.pickup_closed.cannot_order")
        : t("ui.user.dish.add")

  return (
    <div className="flex w-full items-center gap-2.5">
      <QuantityStepper
        value={qty}
        onChange={setQty}
        decrementTestId="dish-qty-decrement"
        incrementTestId="dish-qty-increment"
        valueTestId="dish-qty-value"
        disabled={unavailable}
      />
      <Button
        type="button"
        size="lg"
        data-testid="add-to-cart-button"
        onClick={onAdd}
        disabled={unavailable || submitting || blockedReason !== null || !canSubmit}
        className="flex-1 justify-between"
      >
        <span>{label}</span>
        <span className="tabular-nums">{formatMoney(total, { currency })}</span>
      </Button>
    </div>
  )
}

function ServiceTypeOption({
  active,
  onClick,
  icon,
  label,
  testId,
}: {
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={cn(
        "flex items-center gap-2 rounded-xl border p-3 text-sm font-medium transition-colors",
        active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
      )}
    >
      <span className={cn(active ? "text-primary" : "text-muted-foreground")}>{icon}</span>
      {label}
    </button>
  )
}

export function Group({
  title,
  subtitle,
  optional = false,
  children,
}: {
  title: string
  subtitle?: string
  optional?: boolean
  children: ReactNode
}) {
  return (
    <section>
      <header className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {optional && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {t("ui.react.features.user.dish_detail.optional")}
          </span>
        )}
      </header>
      {children}
    </section>
  )
}
